import { version } from '../../package.json'
import { resolveXmaxReportEnvironment, XMAX_SDK_BUILD_ENV } from '../build-config'
import {
  createTelemetryPayload,
  isTelemetryEndpointConfigured,
  sendTelemetryBeacon,
  sendTelemetryPayload,
  telemetryEnvelopeKey,
  TelemetryTransportError,
  XMAX_TELEMETRY_DEFAULT_ENDPOINT,
  type XmaxTelemetryProbeMeta,
} from './backend'
import { configureRemoteProbeConfig, XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT } from './probe-config'
import { acknowledgeReport, configureReportOutbox, persistReport, releaseReport, wakeReportOutbox } from './outbox'

export type XmaxTelemetryParams = Record<string, string | number | boolean | null | undefined>
export type XmaxReportSource = 'open_platform' | 'external_developer'
export type XmaxTelemetryEventName =
  | 'sdk_api_request'
  | 'sdk_capture_get_user_media_start'
  | 'sdk_capture_get_user_media_end'
  | 'sdk_asset_sts_request'
  | 'sdk_asset_sts_response'
  | 'sdk_asset_upload_start'
  | 'sdk_asset_upload_end'
  | 'sdk_api_session_request'
  | 'sdk_api_session_response'
  | 'sdk_rtc_join_request'
  | 'sdk_rtc_join_response'
  | 'sdk_rtc_publish_local_track'
  | 'sdk_generation_request_sent'
  | 'sdk_generation_ack_received'
  | 'sdk_rtc_subscribe_remote_track'
  | 'sdk_render_output_first_frame'
  | 'sdk_context_set_request'
  | 'sdk_context_set_response'
  | 'sdk_session_connect'
  | 'sdk_session_state_change'
  | 'sdk_session_error'
  | 'sdk_session_disconnect'
  | 'sdk_rtc_stats_snapshot'
  | 'sdk_rtc_remote_state_change'
  | 'sdk_rtc_connection_state_change'
  | 'sdk_rtc_token_state_change'
  | 'sdk_rtc_teardown'
  | 'sdk_probe_degraded'
  | 'sdk_probe_health'
  | 'sdk_probe_buffer_overflow'
export type XmaxTelemetryEvent = { name: XmaxTelemetryEventName; params: XmaxTelemetryParams }
export type XmaxTelemetryOptions = {
  /** Override the region-specific Xmax ingestion endpoint. */
  endpoint?: string
  /** Override the region-specific remote probe configuration endpoint, or disable remote config. */
  configEndpoint?: string | false
  /** Optional extra public fields. Never pass credentials or media/prompt content. */
  commonParams?: XmaxTelemetryParams
  /** @internal Test/custom transport hook. It runs outside the business request path. */
  reporter?: (events: readonly XmaxTelemetryEvent[]) => void | Promise<void>
}

export const TELEMETRY_LIMITS = {
  queue: 500,
  batch: 50,
  targetBatchBytes: 8_000,
  maxBatchBytes: 48_000,
  flushMs: 2_000,
  ttlMs: 5 * 60_000,
  transportTimeoutMs: 3_000,
  maxAttempts: 3,
  retryBaseMs: 1_000,
  circuitBreakMs: 30_000,
  pageMinIntervalMs: 500,
  sessionMinIntervalMs: 2_000,
} as const

type QueuedEvent = XmaxTelemetryEvent & { queuedAt: number; critical: boolean; estimatedBytes: number }
type PendingBatch = {
  id: string
  envelopeKey: string
  seq: number
  critical: boolean
  queued: QueuedEvent[]
  events: XmaxTelemetryEvent[]
  body: string
  attempts: number
}

let config: XmaxTelemetryOptions = {
  endpoint: XMAX_TELEMETRY_DEFAULT_ENDPOINT,
  configEndpoint: XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT,
}
let enabled = true
let generation = 0
let queue: QueuedEvent[] = []
let queueBytes = 0
let pending: PendingBatch | undefined
let timer: ReturnType<typeof setTimeout> | undefined
let retryTimer: ReturnType<typeof setTimeout> | undefined
let sending = false
let suspendedUntil = 0
let forceDrain = false
let pageId: string | undefined
let listeningLifecycle = false
let remoteConfigStarted = false
let lastPageAttemptAt = Number.NEGATIVE_INFINITY
const lastEnvelopeAttemptAt = new Map<string, number>()
const packetSequences = new Map<string, number>()
const reportBytes = new Map<string, number>()
const rtcSnapshots = new Map<string, (generationId: string | null) => XmaxTelemetryParams | null>()
const terminalEnvelopes = new Set<string>()

export function registerTelemetrySnapshot(traceId: string, read: (generationId: string | null) => XmaxTelemetryParams | null): () => void {
  const key = `trace:${traceId}`
  rtcSnapshots.set(key, read)
  return () => { rtcSnapshots.delete(key) }
}

export function telemetryReportedBytes(traceId: string): number {
  return reportBytes.get(`trace:${traceId}`) ?? 0
}
const probeHealth = new Map<string, XmaxTelemetryProbeMeta>()
const queuedBytesByEnvelope = new Map<string, number>()
const health = {
  events_enqueued: 0,
  events_dropped: 0,
  events_accepted: 0,
  batches_accepted: 0,
  batches_beaconed: 0,
  send_attempts: 0,
  send_retries: 0,
  send_failures: 0,
  queue_peak_count: 0,
}
const CRITICAL_EVENTS = new Set<XmaxTelemetryEventName>([
  'sdk_render_output_first_frame',
  'sdk_session_error',
  'sdk_session_disconnect',
  'sdk_rtc_connection_state_change',
  'sdk_rtc_token_state_change',
  'sdk_rtc_teardown',
])

export function telemetryTimer(callback: () => void, ms: number): ReturnType<typeof setTimeout> {
  const handle = setTimeout(callback, ms)
  ;(handle as unknown as { unref?: () => void }).unref?.()
  return handle
}

function clearSchedule(): void {
  if (timer) clearTimeout(timer)
  if (retryTimer) clearTimeout(retryTimer)
  timer = undefined
  retryTimer = undefined
}

function ensureLifecycleListeners(): void {
  if (listeningLifecycle || typeof window === 'undefined') return
  listeningLifecycle = true
  window.addEventListener?.('online', () => {
    if (enabled) wakeReportOutbox()
    if (!enabled || (!pending && !queue.length)) return
    suspendedUntil = 0
    clearSchedule()
    timer = telemetryTimer(() => { timer = undefined; void flush() }, 0)
  })
  window.addEventListener?.('pagehide', () => flushTelemetry(true))
  if (typeof document !== 'undefined') {
    document.addEventListener?.('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushTelemetry(true)
    })
  }
}

/** Configure once before creating clients; false opts out and drops pending diagnostics. */
export function configureXmaxTelemetry(options: XmaxTelemetryOptions | false): void {
  generation++
  enabled = options !== false
  queue = []
  if (pending) releaseReport(pending.id)
  pending = undefined
  sending = false
  suspendedUntil = 0
  forceDrain = false
  lastPageAttemptAt = Number.NEGATIVE_INFINITY
  remoteConfigStarted = false
  lastEnvelopeAttemptAt.clear()
  packetSequences.clear()
  reportBytes.clear()
  rtcSnapshots.clear()
  terminalEnvelopes.clear()
  probeHealth.clear()
  queuedBytesByEnvelope.clear()
  queueBytes = 0
  clearSchedule()
  Object.keys(health).forEach(key => { health[key as keyof typeof health] = 0 })
  if (options !== false) {
    config = {
      endpoint: XMAX_TELEMETRY_DEFAULT_ENDPOINT,
      configEndpoint: XMAX_TELEMETRY_DEFAULT_CONFIG_ENDPOINT,
      ...options,
      commonParams: sanitizeParams(options.commonParams ?? {}),
    }
    ensureLifecycleListeners()
    configureReportOutbox(config.reporter ? undefined : config.endpoint)
  } else {
    configureRemoteProbeConfig(false)
    configureReportOutbox(undefined, true)
  }
}

export function telemetryEnabled(): boolean {
  const active = enabled
    && Boolean(config.reporter || isTelemetryEndpointConfigured(config.endpoint))
    && typeof window !== 'undefined'
    && typeof document !== 'undefined'
  return active
}

export function telemetryId(): string {
  const nativeId = globalThis.crypto?.randomUUID?.()
  if (nativeId) return nativeId
  const bytes = new Uint8Array(16)
  if (typeof globalThis.crypto?.getRandomValues === 'function') globalThis.crypto.getRandomValues(bytes)
  else for (let index = 0; index < bytes.length; index++) bytes[index] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

export function telemetryReportSource(value: unknown): XmaxReportSource {
  return value === 'open_platform' ? 'open_platform' : 'external_developer'
}

export function telemetryPageId(): string {
  return pageId ??= telemetryId()
}

export function telemetryHealth(): XmaxTelemetryParams {
  return { ...health, queue_count: queue.length + (pending?.events.length ?? 0) }
}

export function apiHost(baseUrl: string): string {
  try { return new URL(baseUrl).host } catch { return 'invalid' }
}

export function telemetryEnvironment(reportSource: unknown): 'prod' | 'test' | 'dev' {
  return resolveXmaxReportEnvironment(reportSource, XMAX_SDK_BUILD_ENV)
}

export function sanitizeParams(params: XmaxTelemetryParams): XmaxTelemetryParams {
  const result: XmaxTelemetryParams = {}
  let count = 0
  for (const key of Object.keys(params)) {
    if (count >= 220) break
    if (
      !/^[a-z][a-z0-9_]{0,63}$/.test(key)
      || ['region', 'sdk_region', 'region_form'].includes(key)
      || (/token|api_?key|authorization|password|prompt|email|account_info|ref_image|url/i.test(key) && key !== 'api_key_md5')
      || /^(body|headers|request_body|response_body|request_headers|response_headers)$/.test(key)
    ) continue
    const value = params[key]
    if (typeof value === 'string') result[key] = value.slice(0, 200)
    else if (typeof value === 'number' && Number.isFinite(value)) result[key] = value
    else if (typeof value === 'boolean' || value === null) result[key] = value
    else continue
    count++
  }
  return result
}

function schedule(delay: number = TELEMETRY_LIMITS.flushMs): void {
  if (timer || retryTimer || sending || (!pending && !queue.length) || !telemetryEnabled()) return
  const suspension = Math.max(0, suspendedUntil - performance.now())
  timer = telemetryTimer(() => { timer = undefined; void flush() }, Math.max(delay, suspension))
}

function healthFor(key: string): XmaxTelemetryProbeMeta {
  let value = probeHealth.get(key)
  if (!value) {
    value = { sent: 0, dropped: 0, retried: 0, exempted: 0 }
    probeHealth.set(key, value)
  }
  return value
}

function noteDropped(event: QueuedEvent): void {
  health.events_dropped++
  healthFor(telemetryEnvelopeKey(event)).dropped++
}

function dropOldestNonCritical(): boolean {
  const disposable = queue.findIndex(event => !event.critical)
  if (disposable < 0) return false
  const dropped = removeQueuedAt(disposable)
  noteDropped(dropped)
  return true
}

function makeQueuedEvent(name: XmaxTelemetryEventName, params: XmaxTelemetryParams, queuedAt: number): QueuedEvent {
  const event: QueuedEvent = { name, queuedAt, critical: CRITICAL_EVENTS.has(name), estimatedBytes: 0, params: {
    ...sanitizeParams(config.commonParams ?? {}),
    ...sanitizeParams(params),
    generation_id: typeof params.generation_id === 'string' ? params.generation_id.slice(0, 200) : null,
    event_id: telemetryId(),
    schema_version: 1,
    xmax_sdk_version: version,
    page_id: params.page_id ?? telemetryPageId(),
    event_time_ms: params.event_time_ms ?? Date.now(),
  } }
  if (name === 'sdk_api_request' && params.ok === false) event.critical = true
  event.estimatedBytes = utf8Bytes(JSON.stringify({ name: event.name, params: event.params }))
  return event
}

function addQueued(event: QueuedEvent): void {
  queue.push(event)
  queueBytes += event.estimatedBytes
  const key = telemetryEnvelopeKey(event)
  queuedBytesByEnvelope.set(key, (queuedBytesByEnvelope.get(key) ?? 0) + event.estimatedBytes)
}

function removeQueuedAt(index: number): QueuedEvent {
  const [event] = queue.splice(index, 1)
  queueBytes -= event.estimatedBytes
  const key = telemetryEnvelopeKey(event)
  const remaining = Math.max(0, (queuedBytesByEnvelope.get(key) ?? 0) - event.estimatedBytes)
  if (remaining > 0) queuedBytesByEnvelope.set(key, remaining)
  else queuedBytesByEnvelope.delete(key)
  return event
}

function overflowEvent(source: QueuedEvent, queuedAt: number): QueuedEvent {
  const params = source.params
  return makeQueuedEvent('sdk_probe_buffer_overflow', {
    trace_id: params.trace_id,
    call_id: params.call_id,
    trace_start_time_ms: params.trace_start_time_ms,
    session_id: params.session_id,
    generation_id: params.generation_id,
    page_id: params.page_id,
    api_key_md5: params.api_key_md5,
    env: params.env,
    report_source: params.report_source,
    model: params.model,
    dropped_event: source.name,
    queue_limit: TELEMETRY_LIMITS.queue,
  }, queuedAt)
}

/** Constant bounded work only; it never executes network or host callbacks. */
export function emitTelemetry(name: XmaxTelemetryEventName, params: XmaxTelemetryParams): boolean {
  try { return enqueueTelemetry(name, params) } catch { return false }
}

function enqueueTelemetry(name: XmaxTelemetryEventName, params: XmaxTelemetryParams): boolean {
  if (!telemetryEnabled()) return false
  if (!remoteConfigStarted) {
    remoteConfigStarted = true
    configureRemoteProbeConfig(
      config.configEndpoint,
      telemetryEnvironment(params.report_source ?? config.commonParams?.report_source),
    )
  }
  ensureLifecycleListeners()
  configureReportOutbox(config.reporter ? undefined : config.endpoint)
  const queuedAt = performance.now()
  const event = makeQueuedEvent(name, params, queuedAt)
  const key = telemetryEnvelopeKey(event)
  const bufferedCount = queue.length + (pending?.events.length ?? 0)
  if (name === 'sdk_probe_health') terminalEnvelopes.add(key)
  while (queue.length && queueBytes + event.estimatedBytes > 160_000) {
    if (!dropOldestNonCritical()) { noteDropped(event); return false }
  }
  if (bufferedCount >= TELEMETRY_LIMITS.queue) {
    let freed = dropOldestNonCritical()
    if (!freed && event.critical && queue.length > 0) {
      noteDropped(removeQueuedAt(0))
      freed = true
    }
    if (!freed) {
      health.events_dropped++
      healthFor(key).dropped++
      return false
    }
    const alreadyReported = queue.some(item => item.name === 'sdk_probe_buffer_overflow' && telemetryEnvelopeKey(item) === key)
      || pending?.queued.some(item => item.name === 'sdk_probe_buffer_overflow' && telemetryEnvelopeKey(item) === key)
    if (!alreadyReported) {
      if (queue.length + (pending?.events.length ?? 0) >= TELEMETRY_LIMITS.queue) dropOldestNonCritical()
      addQueued(overflowEvent(event, queuedAt))
    }
  }
  if (queue.length + (pending?.events.length ?? 0) >= TELEMETRY_LIMITS.queue && !dropOldestNonCritical()) {
    health.events_dropped++
    healthFor(key).dropped++
    return false
  }
  addQueued(event)
  if (name === 'sdk_probe_buffer_overflow') healthFor(key).dropped++
  if (event.critical) healthFor(key).exempted++
  health.events_enqueued++
  health.queue_peak_count = Math.max(health.queue_peak_count, queue.length + (pending?.events.length ?? 0))
  schedule(event.critical ? 0 : TELEMETRY_LIMITS.flushMs)
  const periodicSnapshot = name === 'sdk_rtc_stats_snapshot' && params.snapshot_reason === 'periodic'
  if (!retryTimer && !sending && (periodicSnapshot || queue.length >= TELEMETRY_LIMITS.batch || (queuedBytesByEnvelope.get(key) ?? 0) >= TELEMETRY_LIMITS.targetBatchBytes)) {
    if (timer) clearTimeout(timer)
    timer = undefined
    schedule(0)
  }
  return true
}

function utf8Bytes(value: string): number {
  try { return new Blob([value]).size } catch { return value.length * 3 }
}

function purgeExpired(): void {
  const now = performance.now()
  for (let index = queue.length - 1; index >= 0; index--) {
    if (now - queue[index].queuedAt < TELEMETRY_LIMITS.ttlMs) continue
    noteDropped(removeQueuedAt(index))
  }
  if (pending && now - pending.queued[0].queuedAt >= TELEMETRY_LIMITS.ttlMs) {
    pending.queued.forEach(noteDropped)
    releaseReport(pending.id)
    pending = undefined
  }
}

function createPendingBatch(): PendingBatch | undefined {
  purgeExpired()
  while (queue.length) {
    const envelopeKey = telemetryEnvelopeKey(queue[0])
    const candidateIndexes: number[] = []
    let statsSeen = false
    const generationId = typeof queue[0].params.generation_id === 'string' ? queue[0].params.generation_id : null
    for (let index = 0; index < queue.length && candidateIndexes.length < TELEMETRY_LIMITS.batch; index++) {
      const item = queue[index]
      if (telemetryEnvelopeKey(item) !== envelopeKey) continue
      if ((item.params.generation_id ?? null) !== generationId) break
      if (item.name === 'sdk_rtc_stats_snapshot') {
        if (statsSeen) break
        statsSeen = true
      }
      candidateIndexes.push(index)
    }
    let count = candidateIndexes.length
    let snapshot: XmaxTelemetryParams | null = null
    if (!statsSeen) {
      try { snapshot = rtcSnapshots.get(envelopeKey)?.(generationId) ?? null } catch { /* Fail silent. */ }
    }
    while (count > 0) {
      const selectedIndexes = candidateIndexes.slice(0, count)
      const queued = selectedIndexes.map(index => queue[index])
      const events = queued.map(({ name, params }) => ({ name, params }))
      const id = telemetryId()
      const seq = (packetSequences.get(envelopeKey) ?? 0) + 1
      const currentHealth = healthFor(envelopeKey)
      const envelopeEvents: XmaxTelemetryEvent[] = snapshot && !events.some(event => event.name === 'sdk_rtc_stats_snapshot')
        ? [...events, { name: 'sdk_rtc_stats_snapshot', params: snapshot }]
        : events
      const body = JSON.stringify(createTelemetryPayload(id, envelopeEvents, seq, { ...currentHealth, sent: currentHealth.sent + 1 }))
      const bytes = utf8Bytes(body)
      if (bytes <= TELEMETRY_LIMITS.targetBatchBytes || count === 1) {
        if (bytes > TELEMETRY_LIMITS.maxBatchBytes) {
          const dropped = removeQueuedAt(selectedIndexes[0])
          noteDropped(dropped)
          break
        }
        selectedIndexes.slice().reverse().forEach(removeQueuedAt)
        packetSequences.set(envelopeKey, seq)
        if (!config.reporter) persistReport({ id, body, endpoint: config.endpoint ?? XMAX_TELEMETRY_DEFAULT_ENDPOINT,
          createdAt: Date.now(), critical: queued.some(event => event.critical) })
        return { id, envelopeKey, seq, critical: queued.some(event => event.critical), queued, events, body, attempts: 0 }
      }
      count = Math.max(1, Math.floor(count / 2))
    }
  }
  return undefined
}

function acceptPending(beaconed: boolean): void {
  if (!pending) return
  const envelopeKey = pending.envelopeKey
  const terminal = terminalEnvelopes.has(envelopeKey)
  healthFor(envelopeKey).sent++
  health.events_accepted += pending.events.length
  health.batches_accepted++
  if (beaconed) health.batches_beaconed++
  // Beacon acceptance is not a server acknowledgement; retain a replay copy.
  if (beaconed) releaseReport(pending.id)
  else acknowledgeReport(pending.id)
  pending = undefined
  if (terminal && !queue.some(event => telemetryEnvelopeKey(event) === envelopeKey)) {
    lastEnvelopeAttemptAt.delete(envelopeKey)
    packetSequences.delete(envelopeKey)
    probeHealth.delete(envelopeKey)
    reportBytes.delete(envelopeKey)
    terminalEnvelopes.delete(envelopeKey)
  }
}

function tryBeacon(): boolean {
  if (!telemetryEnabled() || sending || config.reporter) return false
  pending ??= createPendingBatch()
  if (!pending) return false
  const bytes = utf8Bytes(pending.body)
  const used = reportBytes.get(pending.envelopeKey) ?? 0
  const accepted = sendTelemetryBeacon(config.endpoint ?? XMAX_TELEMETRY_DEFAULT_ENDPOINT, pending.body, TELEMETRY_LIMITS.maxBatchBytes)
  if (!accepted) return false
  reportBytes.set(pending.envelopeKey, used + bytes)
  acceptPending(true)
  schedule()
  return true
}

/** Flushes asynchronously. `preferBeacon` is for page-hide lifecycle events. */
export function flushTelemetry(preferBeacon = false): void {
  if (timer) clearTimeout(timer)
  if (retryTimer) clearTimeout(retryTimer)
  timer = undefined
  retryTimer = undefined
  forceDrain = true
  if (preferBeacon) {
    let sent = false
    for (let count = 0; count < 3 && tryBeacon(); count++) sent = true
    if (sent) { forceDrain = false; return }
  }
  schedule(0)
}

function retryDelay(attempt: number, requested?: number): number {
  if (typeof requested === 'number') return requested
  const ceiling = TELEMETRY_LIMITS.retryBaseMs * 2 ** Math.max(0, attempt - 1)
  return Math.round(ceiling + Math.random() * ceiling)
}

function scheduleRetry(delay: number): void {
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = telemetryTimer(() => { retryTimer = undefined; void flush() }, delay)
}

async function flush(): Promise<void> {
  if (!telemetryEnabled() || sending) return
  if (performance.now() < suspendedUntil) { schedule(); return }
  pending ??= createPendingBatch()
  if (!pending) return
  const now = performance.now()
  const pageWait = Math.max(0, TELEMETRY_LIMITS.pageMinIntervalMs - (now - lastPageAttemptAt))
  const envelopeWait = pending.critical
    ? 0
    : Math.max(0, TELEMETRY_LIMITS.sessionMinIntervalMs - (now - (lastEnvelopeAttemptAt.get(pending.envelopeKey) ?? Number.NEGATIVE_INFINITY)))
  const rateLimitWait = Math.max(pageWait, envelopeWait)
  if (rateLimitWait > 0) { schedule(rateLimitWait); return }
  const currentGeneration = generation
  const currentConfig = config
  const current = pending
  const bytes = utf8Bytes(current.body)
  const used = reportBytes.get(current.envelopeKey) ?? 0
  reportBytes.set(current.envelopeKey, used + bytes)
  current.attempts++
  lastPageAttemptAt = now
  lastEnvelopeAttemptAt.set(current.envelopeKey, now)
  health.send_attempts++
  sending = true
  let deadline: ReturnType<typeof setTimeout> | undefined
  try {
    if (currentConfig.reporter) {
      await Promise.race([
        currentConfig.reporter(current.events),
        new Promise<never>((_, reject) => {
          deadline = telemetryTimer(() => reject(new TelemetryTransportError(true)), TELEMETRY_LIMITS.transportTimeoutMs)
        }),
      ])
    } else {
      await sendTelemetryPayload(
        currentConfig.endpoint ?? XMAX_TELEMETRY_DEFAULT_ENDPOINT,
        current.body,
        TELEMETRY_LIMITS.transportTimeoutMs,
      )
    }
    if (generation === currentGeneration && pending === current) acceptPending(false)
  } catch (error) {
    if (generation === currentGeneration && pending === current) {
      health.send_failures++
      const retryable = !(error instanceof TelemetryTransportError) || error.retryable
      if (retryable && current.attempts < TELEMETRY_LIMITS.maxAttempts && performance.now() - current.queued[0].queuedAt < TELEMETRY_LIMITS.ttlMs) {
        health.send_retries++
        healthFor(current.envelopeKey).retried++
        forceDrain = false
        scheduleRetry(retryDelay(current.attempts, error instanceof TelemetryTransportError ? error.retryAfterMs : undefined))
      } else {
        current.queued.forEach(noteDropped)
        if (retryable) releaseReport(current.id)
        else acknowledgeReport(current.id)
        pending = undefined
        if (current.events.some(event => event.name === 'sdk_probe_health')) {
          lastEnvelopeAttemptAt.delete(current.envelopeKey)
          packetSequences.delete(current.envelopeKey)
          probeHealth.delete(current.envelopeKey)
        }
        suspendedUntil = performance.now() + TELEMETRY_LIMITS.circuitBreakMs
      }
    }
  } finally {
    if (deadline) clearTimeout(deadline)
    sending = false
    if (!pending && !queue.length) forceDrain = false
    if (!retryTimer) schedule(forceDrain ? 0 : TELEMETRY_LIMITS.flushMs)
  }
}
